import { useEffect, useRef } from "react";

export const useMapStyleManager = ({
  mapInstance,
  mapboxStyles,
  points,
  polygons,
  showHexagons,
  memoizedHexagons,
  handleMarkerClick,
  addPolygonsToMap,
  addHexagonsToMap,
  addHexagonInteractivity,
  reInitializePoints,
}) => {
  const currentStyleRef = useRef(null);

  const cleanupMapElements = (map) => {
    try {
      const layersToRemove = [
        "pulsing-dots",
        "pulsing-dot-labels",
        "pulsing-dot-layer",
      ];
      layersToRemove.forEach((layerName) => {
        if (map.getLayer(layerName)) {
          map.removeLayer(layerName);
        }
      });

      const sourcesToRemove = ["pulsing-dot-source"];
      sourcesToRemove.forEach((sourceName) => {
        if (map.getSource(sourceName)) {
          map.removeSource(sourceName);
        }
      });

      if (map.listImages) {
        const images = map.listImages();
        images.forEach((imageName) => {
          if (
            imageName.startsWith("pulsing-") ||
            imageName.startsWith("static-")
          ) {
            if (map.hasImage(imageName)) {
              map.removeImage(imageName);
            }
          }
        });
      }
    } catch (error) {}
  };

  const restoreMapLayers = (map) => {
    setTimeout(() => {
      if (reInitializePoints) {
        reInitializePoints(map);
      }

      if (polygons && polygons.length > 0) {
        addPolygonsToMap(map);
      }

      if (showHexagons && points.length > 0) {
        addHexagonsToMap(map, memoizedHexagons, (map) =>
          addHexagonInteractivity(map, handleMarkerClick)
        );
      }
    }, 500);
  };

  useEffect(() => {
    if (!mapInstance || !mapboxStyles) return;

    const map = mapInstance;

    if (!map.getStyle || !map.getStyle()) {
      return;
    }

    const currentStyle = map.getStyle();
    const currentStyleUrl =
      currentStyle?.metadata?.["mapbox:origin"] ||
      currentStyle?.sources?.["mapbox-base"]?.url ||
      "";

    const needsStyleChange = currentStyleRef.current !== mapboxStyles;

    if (needsStyleChange) {
      currentStyleRef.current = mapboxStyles;

      cleanupMapElements(map);

      const onStyleLoad = () => {
        restoreMapLayers(map);
      };

      map.off("style.load", onStyleLoad);
      map.on("style.load", onStyleLoad);

      try {
        map.setStyle(mapboxStyles, { diff: false });
      } catch (error) {
        currentStyleRef.current = null;
      }
    }
  }, [mapboxStyles, mapInstance]);

  return {
    cleanupMapElements,
    restoreMapLayers,
  };
};
